import { FilePlus2 } from 'lucide-react';
import { Modal } from '../../common';
import { CARRIER_TEMPLATES, type TemplateKey } from './schema';

interface TemplatePickerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with a template key, or null to start from a blank peer. */
  onSelect: (templateKey: TemplateKey | null) => void;
}

/**
 * Create-only template chooser. Seeds the SIP peer editor with sensible defaults
 * for a peer type (or a blank peer). Replaces the old wizard's template step.
 */
export function TemplatePicker({ isOpen, onClose, onSelect }: TemplatePickerProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add SIP Peer"
      subtitle="Choose a template to pre-configure settings, then customize in the editor."
      size="lg"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(Object.entries(CARRIER_TEMPLATES) as [TemplateKey, (typeof CARRIER_TEMPLATES)[TemplateKey]][]).map(
            ([key, template]) => {
              const Icon = template.icon;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onSelect(key)}
                  className="p-6 border-2 border-gray-200 rounded-lg text-left transition-all hover:border-blue-300 hover:shadow-md"
                >
                  <Icon className="w-10 h-10 mb-3 text-gray-400" />
                  <h4 className="font-medium text-gray-900 mb-2">{template.name}</h4>
                  {template.description && (
                    <p className="text-sm text-gray-600">{template.description}</p>
                  )}
                </button>
              );
            }
          )}
        </div>

        <button
          type="button"
          onClick={() => onSelect(null)}
          className="flex w-full items-center gap-3 rounded-lg border-2 border-dashed border-gray-200 p-4 text-left transition-colors hover:border-blue-300 hover:bg-gray-50"
        >
          <FilePlus2 className="w-6 h-6 text-gray-400" />
          <div>
            <h4 className="font-medium text-gray-900">Start from scratch</h4>
            <p className="text-sm text-gray-600">Create a blank peer and configure everything manually.</p>
          </div>
        </button>
      </div>
    </Modal>
  );
}
